import React, { useMemo, useCallback } from 'react';
import { Tabs } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { removeTab, setActiveTab } from '../../store/slices/tabsSlice';
import styles from './index.module.css';

// 动态路由映射（使用 React.lazy 的工厂函数）
const routeComponents = {
  'product/management/goods': () => import('../../pages/ProductManagement/GoodsList'),
  'product/management/category': () => import('../../pages/ProductManagement/GoodsCategoryManagement'),
  // 添加更多路由映射
};

// 创建一个缓存组件的高阶组件
const withMemoizedComponent = (importFn) => {
  const LazyComponent = React.lazy(importFn);
  return React.memo((props) => (
    <React.Suspense fallback={<div>加载中...</div>}>
      <LazyComponent {...props} />
    </React.Suspense>
  ));
};

// 缓存路由组件
const memoizedRouteComponents = Object.fromEntries(
  Object.entries(routeComponents).map(([key, importFn]) => [
    key, 
    withMemoizedComponent(importFn)
  ])
);

const TabsContainer = React.memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { panes, activeKey } = useSelector(state => state.tabs);
 
  const onChange = useCallback((key) => {
    dispatch(setActiveTab(key));
    const targetPane = panes.find(pane => pane.key === key);
    if (targetPane) {
      navigate(targetPane.path);
    }
  }, [dispatch, navigate, panes]);

  const onEdit = useCallback((targetKey, action) => {
    if (action === 'remove') {
      dispatch(removeTab(targetKey));
    }
  }, [dispatch]);

  // 使用 useMemo 确保相同的组件只渲染一次，并过滤掉 dashboard
  const uniquePanes = useMemo(() => {
    const seenKeys = new Set();
    return panes.filter(pane => {
      // 过滤掉 dashboard 标签页
      if (pane.key === 'dashboard') {
        return false;
      }
      if (seenKeys.has(pane.key)) {
        return false;
      }
      seenKeys.add(pane.key);
      return true;
    });
  }, [panes]);

  // 缓存渲染的标签页内容
  const tabPaneContent = useMemo(() => {
    return uniquePanes.map(pane => {
      // 动态加载组件
      const Component = memoizedRouteComponents[pane.key];
      return {
        key: pane.key,
        tab: pane.title,
        closable: pane.closable !== false,
        content: Component ? <Component /> : null
      };
    });
  }, [uniquePanes]);

  return (
    <Tabs
      className={styles.tabsContainer}
      type="editable-card"
      onChange={onChange}
      activeKey={activeKey}
      onEdit={onEdit}
      hideAdd
    >
      {tabPaneContent.map(pane => (
        <Tabs.TabPane 
          key={pane.key} 
          tab={pane.tab} 
          closable={pane.closable}
        >
          {pane.content}
        </Tabs.TabPane>
      ))}
    </Tabs>
  );
});

TabsContainer.displayName = 'TabsContainer';

export default TabsContainer; 